"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssistantsHttpService = void 0;
const zod_1 = require("zod");
const types_1 = require("../../types");
const success_response_schema_1 = require("../../types/global/success-response.schema");
const http_service_1 = require("../http.service");
class AssistantsHttpService extends http_service_1.HttpService {
    constructor(apiUrl) {
        super(`${apiUrl}/assistants`);
    }
    getList(payload) {
        const params = {
            page: String(payload.page),
            per_page: String(payload.per_page),
        };
        if (payload.search)
            params.search = payload.search;
        if (payload.contractType)
            params.contractType = payload.contractType;
        if (payload.includeDeactivated)
            params.includeDeactivated = 'true';
        const url = this.getApiUrlOf('', params);
        if (payload.officeIds?.length) {
            const searchParams = new URLSearchParams(url.search);
            for (const officeId of payload.officeIds) {
                searchParams.append('officeIds', officeId);
            }
            url.search = searchParams.toString();
        }
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listMedicalAssistantsApiResponseSchema,
        }).then(({ data }) => data);
    }
    getDetail(id) {
        return this.getEntity(encodeURIComponent(id), {
            schema: types_1.getMedicalAssistantApiResponseSchema,
        });
    }
    create(payload) {
        return this.fetch(this.getApiUrlOf(''), {
            method: 'POST',
            body: payload,
            schema: types_1.getMedicalAssistantApiResponseSchema,
        }).then((r) => r.data);
    }
    update(id, payload) {
        return this.fetch(this.getApiUrlOf(encodeURIComponent(id)), {
            method: 'PATCH',
            body: payload,
            schema: types_1.getMedicalAssistantApiResponseSchema,
        }).then((r) => r.data);
    }
    /**
     * Self-service update of the current assistant's own profile.
     * Targets `PATCH /assistants/me`; the caller is resolved server-side from
     * the auth token, so no id is passed.
     *
     * @param payload - partial profile fields to update
     * @returns the updated medical assistant
     */
    updateMyProfile(payload) {
        return this.fetch(this.getApiUrlOf('me'), {
            method: 'PATCH',
            body: payload,
            schema: types_1.getMedicalAssistantApiResponseSchema,
        }).then((r) => r.data);
    }
    deactivate(id) {
        return this.delete(encodeURIComponent(id), {
            schema: success_response_schema_1.successResponseSchema,
        });
    }
    reactivate(id) {
        return this.post(`${encodeURIComponent(id)}/reactivate`, { schema: success_response_schema_1.successResponseSchema });
    }
    assignOffice(id, officeId) {
        return this.put(`${encodeURIComponent(id)}/offices/${encodeURIComponent(officeId)}`, {
            body: {},
            schema: success_response_schema_1.successResponseSchema,
        });
    }
    unassignOffice(id, officeId) {
        return this.delete(`${encodeURIComponent(id)}/offices/${encodeURIComponent(officeId)}`, { schema: success_response_schema_1.successResponseSchema });
    }
    // Slot types
    listSlotTypes(payload) {
        const params = {};
        if (payload?.scope)
            params.scope = payload.scope;
        if (payload?.officeId)
            params.officeId = payload.officeId;
        const url = this.getApiUrlOf('planning/slot-types', params);
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listSlotTypesApiResponseSchema,
        }).then(({ data }) => data);
    }
    /**
     * Sets the per-office hours of a fixed-catalog slot type, identified by its
     * catalog label. The server upserts the office-scoped override; arbitrary
     * type creation is not supported (the catalog is code-owned).
     */
    setOfficeSlotTypeHours(payload) {
        const url = this.getApiUrlOf('planning/slot-types/office-hours');
        return this.fetch(url, {
            method: 'PUT',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantSlotTypeItemSchema }),
        }).then(({ data }) => data);
    }
    // Templates
    getTemplate(params) {
        const url = this.getApiUrlOf('planning/templates', {
            officeId: params.officeId,
            week: params.week,
        });
        return this.fetch(url, {
            method: 'GET',
            schema: zod_1.z.object({ data: types_1.assistantWeeklyTemplateApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    buildTemplate(payload) {
        const url = this.getApiUrlOf('planning/templates');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantWeeklyTemplateApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    upsertTemplateEntry(payload) {
        const url = this.getApiUrlOf('planning/templates/entries');
        return this.fetch(url, {
            method: 'PUT',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantWeeklyTemplateApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    deleteTemplateEntry(params) {
        const url = this.getApiUrlOf('planning/templates/entries', {
            officeId: params.officeId,
            week: params.week,
            weekday: String(params.weekday),
        });
        return this.fetch(url, {
            method: 'DELETE',
            schema: zod_1.z.object({ data: types_1.assistantWeeklyTemplateApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    // Planning
    generatePlanningWeek(payload) {
        const url = this.getApiUrlOf('planning/weeks/generate');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: types_1.generateAssistantPlanningWeekApiResponseSchema,
        }).then(({ data }) => data);
    }
    getPlanningGrid(payload) {
        const url = this.getApiUrlOf('planning/grid', { week: payload.week });
        const searchParams = new URLSearchParams(url.search);
        for (const id of payload.officeIds ?? []) {
            searchParams.append('officeIds', id);
        }
        for (const id of payload.assistantIds ?? []) {
            searchParams.append('assistantIds', id);
        }
        url.search = searchParams.toString();
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.getPlanningGridApiResponseSchema,
        }).then(({ data }) => data);
    }
    getMyPlanningGrid(payload) {
        const url = this.getApiUrlOf('planning/me/grid', { week: payload.week });
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.getPlanningGridApiResponseSchema,
        }).then(({ data }) => data);
    }
    // Programmings
    programWeek(payload) {
        const url = this.getApiUrlOf('planning/programmings');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: types_1.programWeekApiResponseSchema,
        }).then(({ data }) => data);
    }
    listOfficeProgrammings(payload) {
        const params = { officeId: payload.officeId };
        if (payload.page)
            params.page = String(payload.page);
        const url = this.getApiUrlOf('planning/programmings', params);
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listOfficeProgrammingsApiResponseSchema,
        }).then(({ data }) => data);
    }
    listProgrammingHistory(payload) {
        const params = { officeId: payload.officeId };
        if (payload.page)
            params.page = String(payload.page);
        if (payload.perPage)
            params.perPage = String(payload.perPage);
        const url = this.getApiUrlOf('planning/programmings/history', params);
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listProgrammingHistoryApiResponseSchema,
        }).then(({ data }) => data);
    }
    overrideSlot(id, payload) {
        const url = this.getApiUrlOf(`planning/slots/${encodeURIComponent(id)}`);
        return this.fetch(url, {
            method: 'PATCH',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantPlannedSlotApiResponseSchema }),
        }).then(({ data }) => data);
    }
    createSlot(payload) {
        const url = this.getApiUrlOf('planning/slots');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantPlannedSlotApiResponseSchema }),
        }).then(({ data }) => data);
    }
    setSlotPauses(id, payload) {
        const url = this.getApiUrlOf(`planning/slots/${encodeURIComponent(id)}/pauses`);
        return this.fetch(url, {
            method: 'PUT',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantPlannedSlotApiResponseSchema }),
        }).then(({ data }) => data);
    }
    // Absences
    submitAbsenceRequest(payload) {
        const url = this.getApiUrlOf('me/absences');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantAbsenceRequestApiResponseSchema }),
        }).then(({ data }) => data);
    }
    listMyAbsences(payload) {
        const params = {
            page: String(payload.page),
            per_page: String(payload.per_page),
        };
        const url = this.getApiUrlOf('me/absences', params);
        if (payload.types?.length || payload.statuses?.length) {
            const searchParams = new URLSearchParams(url.search);
            for (const type of payload.types ?? []) {
                searchParams.append('types', type);
            }
            for (const status of payload.statuses ?? []) {
                searchParams.append('statuses', status);
            }
            url.search = searchParams.toString();
        }
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listAbsenceHistoryApiResponseSchema,
        }).then(({ data }) => data);
    }
    listPendingAbsenceRequests() {
        const url = this.getApiUrlOf('absences/pending');
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listPendingAbsenceRequestsApiResponseSchema,
        }).then(({ data }) => data);
    }
    approveAbsenceRequest(id, payload = {}) {
        const url = this.getApiUrlOf(`absences/${encodeURIComponent(id)}/approve`);
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantAbsenceRequestApiResponseSchema }),
        }).then(({ data }) => data);
    }
    rejectAbsenceRequest(id, payload = {}) {
        const url = this.getApiUrlOf(`absences/${encodeURIComponent(id)}/reject`);
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantAbsenceRequestApiResponseSchema }),
        }).then(({ data }) => data);
    }
    listAbsenceHistory(payload) {
        const params = {
            page: String(payload.page),
            per_page: String(payload.per_page),
        };
        if (payload.search)
            params.search = payload.search;
        if (payload.assistantId)
            params.assistantId = payload.assistantId;
        if (payload.period)
            params.period = JSON.stringify(payload.period);
        const url = this.getApiUrlOf('absences/history', params);
        if (payload.types?.length || payload.statuses?.length) {
            const searchParams = new URLSearchParams(url.search);
            for (const type of payload.types ?? []) {
                searchParams.append('types', type);
            }
            for (const status of payload.statuses ?? []) {
                searchParams.append('statuses', status);
            }
            url.search = searchParams.toString();
        }
        return this.fetch(url, {
            method: 'GET',
            schema: types_1.listAbsenceHistoryApiResponseSchema,
        }).then(({ data }) => data);
    }
    createAdminAbsence(assistantId, payload) {
        const url = this.getApiUrlOf(`${encodeURIComponent(assistantId)}/absences`);
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.assistantAbsenceRequestApiResponseSchema }),
        }).then(({ data }) => data);
    }
    deleteAbsenceRequest(id) {
        const url = this.getApiUrlOf(`absences/${encodeURIComponent(id)}`);
        return this.fetch(url, {
            method: 'DELETE',
            schema: zod_1.z.object({ data: types_1.assistantAbsenceRequestApiResponseSchema }),
        }).then(({ data }) => data);
    }
    // Modulations
    previewModulations(payload) {
        const url = this.getApiUrlOf('modulations/preview');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.previewModulationsApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    createModulations(payload) {
        const url = this.getApiUrlOf('modulations');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.createModulationsApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    initializeModulations(payload) {
        const url = this.getApiUrlOf('modulations/initialize');
        return this.fetch(url, {
            method: 'POST',
            body: payload,
            schema: zod_1.z.object({ data: types_1.initializeModulationsApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    deleteModulation(modulationId) {
        const url = this.getApiUrlOf(`modulations/${encodeURIComponent(modulationId)}`);
        return this.fetch(url, {
            method: 'DELETE',
            schema: zod_1.z.object({ data: types_1.deleteModulationApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    listAssistantModulations(assistantId) {
        const url = this.getApiUrlOf(`modulations/${encodeURIComponent(assistantId)}`);
        return this.fetch(url, {
            method: 'GET',
            schema: zod_1.z.object({ data: types_1.listAssistantModulationsApiResponseSchema }),
        }).then(({ data }) => data.data);
    }
    setDayStatus(assistantId, date, payload) {
        return this.put(`${encodeURIComponent(assistantId)}/days/${encodeURIComponent(date)}/status`, {
            body: payload,
            schema: types_1.setAssistantDayStatusApiResponseSchema,
        });
    }
}
exports.AssistantsHttpService = AssistantsHttpService;
